/**
 * The widget Embed aggregate (D426 W-EMBED-API, ADR-0067).
 *
 * An embed is one site's configuration of the Central widget: a display name for the Admin, a PUBLIC key the site pastes
 * in its `<script>` tag, the exact web origins allowed to use that key, a few visitor-facing settings and a status.
 *
 * The key is public like a publishable key — it identifies a configuration, it is not a secret, and protection comes
 * from the origin allowlist, the rate limits and the per-session budget, never from hiding it. Its SHA-256 is the lookup
 * handle of every public request, plus an 8-character display prefix. Amendment D429 (ADR-0067): the full key is ALSO
 * kept (`publicKey`), because it is already public (it sits in the partner's HTML) and the operator must be able to copy
 * the `<script>` snippet at any time, not only at creation; it is dropped on revoke. Rotation mints a new key and the old
 * one stops matching immediately (its hash is gone); revoking refuses the key from then on, and a revoked embed never
 * comes back (a new embed is created instead).
 *
 * Pure: randomness is injected (32 bytes from a CSPRNG), hashing is the platform-neutral `sha256Hex`, time is an ISO
 * string. Neutral: no country, locale or product default is invented here — a locale is optional data.
 */
import { err, ok, sha256Hex } from '@vello/core';
import { refuse } from './reasons.js';
import { normalizeOriginList } from './origin.js';
import { validatePlainText } from './text.js';
import { LEGACY_WIDGET_SUBJECTS, WIDGET_AUDIENCES, audienceOfLegacySubject, isWidgetAudience, legacySubjectsOf, topicsOf } from './intake.js';
export const WIDGET_KEY_PREFIX = 'vw_pk_';
export const WIDGET_KEY_RANDOM_BYTES = 32;
/** Characters of the random part kept for display (`vw_pk_ab12cd34…`). */
export const WIDGET_KEY_DISPLAY_CHARS = 8;
export const WIDGET_POSITIONS = ['right', 'left'];
export const EMBED_STATUSES = ['ACTIVE', 'REVOKED'];
export const WIDGET_NAME_MAX_CHARS = 120;
export const WIDGET_CONSENT_TEXT_MAX_CHARS = 1000;
const LOCALE_TAG = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8}){0,3}$/;
const B64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
/** Unpadded base64url of a byte array (no Buffer/btoa — runs on any runtime). */
export function base64Url(bytes) {
    let out = '';
    let i = 0;
    for (; i + 2 < bytes.length; i += 3) {
        const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
        out += B64URL[(n >> 18) & 63] + B64URL[(n >> 12) & 63] + B64URL[(n >> 6) & 63] + B64URL[n & 63];
    }
    const rest = bytes.length - i;
    if (rest === 1) {
        const n = bytes[i] << 16;
        out += B64URL[(n >> 18) & 63] + B64URL[(n >> 12) & 63];
    }
    else if (rest === 2) {
        const n = (bytes[i] << 16) | (bytes[i + 1] << 8);
        out += B64URL[(n >> 18) & 63] + B64URL[(n >> 12) & 63] + B64URL[(n >> 6) & 63];
    }
    return out;
}
/** The stored hash of a presented key (the lookup handle). */
export function hashWidgetKey(publicKey) {
    return sha256Hex(publicKey);
}
/** Shape check only (no lookup): `vw_pk_` + 43 base64url characters. */
export function isWellFormedWidgetKey(v) {
    return typeof v === 'string' && /^vw_pk_[A-Za-z0-9_-]{43}$/.test(v);
}
/** Mint a key from 32 injected random bytes. Throws on a short buffer — a weak key is a programming error. */
export function issueWidgetKey(randomBytes) {
    if (randomBytes.length !== WIDGET_KEY_RANDOM_BYTES)
        throw new RangeError(`widget key needs ${WIDGET_KEY_RANDOM_BYTES} random bytes`);
    const random = base64Url(randomBytes);
    const publicKey = `${WIDGET_KEY_PREFIX}${random}`;
    return { publicKey, keyHash: hashWidgetKey(publicKey), keyPrefix: random.slice(0, WIDGET_KEY_DISPLAY_CHARS) };
}
/** The display form of a stored key: `vw_pk_ab12cd34…`. */
export function displayKey(embed) {
    return `${WIDGET_KEY_PREFIX}${embed.keyPrefix}…`;
}
function validateName(raw) {
    const r = validatePlainText(raw, { maxChars: WIDGET_NAME_MAX_CHARS, allowNewline: false });
    return r.ok ? r : err(refuse('WIDGET_NAME_INVALID', `name must be 1..${WIDGET_NAME_MAX_CHARS} characters of plain text`));
}
/**
 * Validate the visitor-facing settings. Unknown members are ignored; `position` defaults to `right` and `audiences` to
 * every audience. A present-but-invalid member refuses the whole object (`WIDGET_SETTINGS_INVALID`). The pre-D625
 * `subjects` list is still read when `audiences` is absent (an Admin not yet rolled, a stored row): each subject names
 * its audience.
 */
export function normalizeEmbedSettings(raw) {
    const s = raw === undefined ? {} : raw;
    if (typeof s !== 'object' || s === null || Array.isArray(s))
        return err(refuse('WIDGET_SETTINGS_INVALID', 'settings must be an object'));
    const o = s;
    let locale;
    if (o.locale !== undefined && o.locale !== null) {
        if (typeof o.locale !== 'string' || !LOCALE_TAG.test(o.locale))
            return err(refuse('WIDGET_SETTINGS_INVALID', 'locale must be a BCP 47 tag'));
        locale = o.locale;
    }
    const position = o.position ?? 'right';
    if (!WIDGET_POSITIONS.includes(position))
        return err(refuse('WIDGET_SETTINGS_INVALID', 'position must be right or left'));
    let audiences = [...WIDGET_AUDIENCES];
    if (o.audiences !== undefined) {
        if (!Array.isArray(o.audiences) || o.audiences.length === 0 || !o.audiences.every(isWidgetAudience)) {
            return err(refuse('WIDGET_SETTINGS_INVALID', `audiences must be a non-empty subset of ${WIDGET_AUDIENCES.join('|')}`));
        }
        audiences = [...new Set(o.audiences)];
    }
    else if (o.subjects !== undefined) {
        if (!Array.isArray(o.subjects) || o.subjects.length === 0 || !o.subjects.every((x) => LEGACY_WIDGET_SUBJECTS.includes(x))) {
            return err(refuse('WIDGET_SETTINGS_INVALID', `subjects must be a non-empty subset of ${LEGACY_WIDGET_SUBJECTS.join('|')}`));
        }
        audiences = [...new Set(o.subjects.map(audienceOfLegacySubject))];
    }
    let consentText;
    if (o.consentText !== undefined && o.consentText !== null) {
        const c = validatePlainText(o.consentText, { maxChars: WIDGET_CONSENT_TEXT_MAX_CHARS, allowNewline: true });
        if (!c.ok)
            return err(refuse('WIDGET_SETTINGS_INVALID', `consentText must be 1..${WIDGET_CONSENT_TEXT_MAX_CHARS} characters of plain text`));
        consentText = c.value;
    }
    return ok({
        ...(locale !== undefined ? { locale } : {}),
        position: position,
        audiences,
        ...(consentText !== undefined ? { consentText } : {}),
    });
}
/**
 * The settings of a stored embed row, read through the same rules as a write (a row written before D625 carries
 * `subjects`, which is read as its audiences). A row that no longer validates falls back to the defaults — it was valid
 * when written, so this only guards a hand-edited row.
 */
export function readStoredEmbedSettings(raw) {
    const r = normalizeEmbedSettings(raw);
    return r.ok ? r.value : { position: 'right', audiences: [...WIDGET_AUDIENCES] };
}
const MARKET_ID = /^[a-z]{2}:[a-z0-9]+(-[a-z0-9]+)*$/;
/** D449 (E-01): a well-formed market id (`cc:slug`), lower-cased; `null` clears it on an update. */
export function normalizeEmbedMarket(v) {
    if (v === undefined || v === null)
        return ok(undefined);
    if (typeof v !== 'string' || !MARKET_ID.test(v.trim().toLowerCase()))
        return err(refuse('WIDGET_MARKET_INVALID', 'marketId must be a market id like cc:city'));
    return ok(v.trim().toLowerCase());
}
const COUNTRY = /^[A-Za-z]{2}$/;
/** D853: a well-formed ISO 3166-1 alpha-2 country, upper-cased; `undefined`/`null` is "not given". */
export function normalizeEmbedCountry(v) {
    if (v === undefined || v === null)
        return ok(undefined);
    if (typeof v !== 'string' || !COUNTRY.test(v.trim()))
        return err(refuse('WIDGET_COUNTRY_INVALID', 'countryCode must be a two-letter country code'));
    return ok(v.trim().toUpperCase());
}
/** The country a market id belongs to (`cc:slug` -> `CC`). */
export const countryOfMarketId = (marketId) => marketId.slice(0, 2).toUpperCase();
/** The country an embed serves: its stored country, else its market's (an embed written before D853). */
export function embedCountry(e) {
    return e.countryCode ?? (e.marketId !== undefined ? countryOfMarketId(e.marketId) : undefined);
}
/**
 * The city a page names for a COUNTRY embed (`data-market`), as the resolver's page signal: accepted only when it is a served market of the embed's country.
 * A city-fixed embed ignores the page and answers with its own market (`fixed: true`).
 */
export function pageMarketOf(embed, claimed, served) {
    if (embed.marketId !== undefined)
        return { marketId: embed.marketId, fixed: true };
    if (typeof claimed !== 'string')
        return undefined;
    const id = claimed.trim().toLowerCase();
    const country = embedCountry(embed);
    if (country === undefined || !MARKET_ID.test(id) || countryOfMarketId(id) !== country)
        return undefined;
    return served.some((m) => m.id === id) ? { marketId: id, fixed: false } : undefined;
}
/** Create an ACTIVE embed with a fresh key. Returns the embed and the full key (to show once). */
export function createEmbed(input, randomBytes, now) {
    const name = validateName(input.name);
    if (!name.ok)
        return name;
    const origins = normalizeOriginList(input.allowedOrigins);
    if (!origins.ok)
        return origins;
    const settings = normalizeEmbedSettings(input.settings);
    if (!settings.ok)
        return settings;
    const market = normalizeEmbedMarket(input.marketId);
    if (!market.ok)
        return market;
    const country = normalizeEmbedCountry(input.countryCode);
    if (!country.ok)
        return country;
    if (market.value !== undefined && country.value !== undefined && countryOfMarketId(market.value) !== country.value) {
        return err(refuse('WIDGET_COUNTRY_INVALID', 'the market does not belong to that country'));
    }
    const countryCode = country.value ?? (market.value !== undefined ? countryOfMarketId(market.value) : undefined);
    const key = issueWidgetKey(randomBytes);
    return ok({
        publicKey: key.publicKey,
        embed: {
            embedId: input.embedId,
            name: name.value,
            keyHash: key.keyHash,
            keyPrefix: key.keyPrefix,
            publicKey: key.publicKey,
            allowedOrigins: origins.value,
            settings: settings.value,
            ...(market.value !== undefined ? { marketId: market.value } : {}),
            ...(countryCode !== undefined ? { countryCode } : {}),
            status: 'ACTIVE',
            createdBy: input.createdBy,
            createdAt: now,
            updatedAt: now,
        },
    });
}
/** Change the name, origins and/or settings of an ACTIVE embed. A revoked embed is frozen. */
export function updateEmbed(embed, input, now) {
    if (embed.status !== 'ACTIVE')
        return err(refuse('WIDGET_EMBED_REVOKED', 'a revoked embed cannot change'));
    let next = embed;
    if (input.name !== undefined) {
        const name = validateName(input.name);
        if (!name.ok)
            return name;
        next = { ...next, name: name.value };
    }
    if (input.allowedOrigins !== undefined) {
        const origins = normalizeOriginList(input.allowedOrigins);
        if (!origins.ok)
            return origins;
        next = { ...next, allowedOrigins: origins.value };
    }
    if (input.settings !== undefined) {
        const settings = normalizeEmbedSettings(input.settings);
        if (!settings.ok)
            return settings;
        next = { ...next, settings: settings.value };
    }
    if (input.marketId !== undefined) {
        const market = normalizeEmbedMarket(input.marketId);
        if (!market.ok)
            return market;
        const { marketId: _drop, ...rest } = next;
        if (market.value !== undefined && embedCountry(embed) !== undefined && embed.countryCode !== undefined && countryOfMarketId(market.value) !== embed.countryCode) {
            return err(refuse('WIDGET_COUNTRY_INVALID', 'the market does not belong to the embed country'));
        }
        next = market.value !== undefined ? { ...rest, marketId: market.value, countryCode: countryOfMarketId(market.value) } : rest;
    }
    if (input.countryCode !== undefined) {
        const country = normalizeEmbedCountry(input.countryCode);
        if (!country.ok)
            return country;
        if (country.value !== undefined) {
            if (next.marketId !== undefined && countryOfMarketId(next.marketId) !== country.value)
                return err(refuse('WIDGET_COUNTRY_INVALID', 'the market does not belong to that country'));
            next = { ...next, countryCode: country.value };
        }
    }
    return ok({ ...next, updatedAt: now });
}
/** Rotate the key of an ACTIVE embed: the new key is returned once; the old hash is gone, so the old key stops at once. */
export function rotateEmbedKey(embed, randomBytes, now) {
    if (embed.status !== 'ACTIVE')
        return err(refuse('WIDGET_EMBED_REVOKED', 'a revoked embed cannot rotate its key'));
    const key = issueWidgetKey(randomBytes);
    return ok({ publicKey: key.publicKey, embed: { ...embed, keyHash: key.keyHash, keyPrefix: key.keyPrefix, publicKey: key.publicKey, rotatedAt: now, updatedAt: now } });
}
/**
 * Revoke an embed (idempotent: an already-revoked embed is returned unchanged). The retrievable key is dropped (D429): a
 * revoked embed never shows a snippet again; its display prefix and hash stay for the record.
 */
export function revokeEmbed(embed, now) {
    if (embed.status === 'REVOKED')
        return embed;
    const { publicKey: _dropped, ...rest } = embed;
    return { ...rest, status: 'REVOKED', revokedAt: now, updatedAt: now };
}
export function publicEmbedSettings(embed) {
    return {
        embedId: embed.embedId,
        ...(embed.settings.locale !== undefined ? { locale: embed.settings.locale } : {}),
        position: embed.settings.position,
        audiences: embed.settings.audiences.map((audience) => ({ audience, topics: topicsOf(audience) })),
        subjects: legacySubjectsOf(embed.settings.audiences),
        ...(embed.settings.consentText !== undefined ? { consentText: embed.settings.consentText } : {}),
        requireConsent: true,
        ...(embedCountry(embed) !== undefined ? { countryCode: embedCountry(embed) } : {}),
        ...(embed.marketId !== undefined ? { fixedMarketId: embed.marketId } : {}),
    };
}
/** The version a visitor's consent record carries: a digest of the exact consent text shown (or the built-in text). */
export function consentVersionOf(embed) {
    return `widget-consent:${sha256Hex(embed.settings.consentText ?? '').slice(0, 16)}`;
}
/** The embed gate of a public request: the key must resolve to an ACTIVE embed. Origin is checked by the caller. */
export function assertEmbedActive(embed) {
    if (embed === undefined)
        return err(refuse('WIDGET_KEY_INVALID', 'unknown widget key'));
    if (embed.status !== 'ACTIVE')
        return err(refuse('WIDGET_KEY_REVOKED', 'this widget key was revoked'));
    return ok(embed);
}
//# sourceMappingURL=embed.js.map